import { supabase } from '@/lib/supabase'
import { getAllowedSymbolsForCurrentUser } from '@/modules/plans/services/planAssetPolicyService'

export type OportunidadesStats = {
  totalActive: number
  longActive: number
  shortActive: number
  monitoringActive: number
}

export type OportunidadesGapStats = {
  totalToday: number
  visibleToday: number
  missedToday: number
  hasAssetLimit: boolean
}

export async function fetchOportunidadesStats(): Promise<OportunidadesStats> {
  let allowed: string[] | null = null
  try {
    allowed = await getAllowedSymbolsForCurrentUser()
  } catch {
    allowed = null
  }

  const normAllowed = allowed && allowed.length
    ? Array.from(new Set(allowed.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean)))
    : null

  const baseOpp = supabase
    .from('v_trading_opportunities_dedup')
    .select('id', { count: 'exact', head: true })
    .eq('is_published', true)
  const baseLong = supabase
    .from('v_trading_opportunities_dedup')
    .select('id', { count: 'exact', head: true })
    .eq('is_published', true)
    .eq('direction_norm', 'bullish')
  const baseShort = supabase
    .from('v_trading_opportunities_dedup')
    .select('id', { count: 'exact', head: true })
    .eq('is_published', true)
    .eq('direction_norm', 'bearish')
  const baseMonitoring = supabase
    .from('active_monitoring')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'active')

  const totalQ = normAllowed ? baseOpp.in('symbol', normAllowed) : baseOpp
  const longQ = normAllowed ? baseLong.in('symbol', normAllowed) : baseLong
  const shortQ = normAllowed ? baseShort.in('symbol', normAllowed) : baseShort
  const monitoringQ = normAllowed ? baseMonitoring.in('symbol', normAllowed) : baseMonitoring

  const [total, longCnt, shortCnt, monitoring] = await Promise.all([
    totalQ,
    longQ,
    shortQ,
    monitoringQ,
  ])

  return {
    totalActive: total.count ?? 0,
    longActive: longCnt.count ?? 0,
    shortActive: shortCnt.count ?? 0,
    monitoringActive: monitoring.count ?? 0,
  }
}

export async function fetchOportunidadesGapStats(): Promise<OportunidadesGapStats> {
  let hasAssetLimit = false
  try {
    const allowed = await getAllowedSymbolsForCurrentUser()
    hasAssetLimit = Array.isArray(allowed) && allowed.length > 0
  } catch {
    hasAssetLimit = false
  }

  // Preferir versão cumulativa; se não existir no backend, fallback para a versão simples
  let rpcName = 'user_daily_missed_opportunities_cumulative'
  let data: any
  let error: any

  let res = await supabase.rpc(rpcName)
  data = res.data
  error = res.error

  // Fallback para função original caso a cumulativa ainda não exista
  if (error && String(error.message || '').toLowerCase().includes('function public.user_daily_missed_opportunities_cumulative')) {
    rpcName = 'user_daily_missed_opportunities'
    const res2 = await supabase.rpc(rpcName)
    data = res2.data
    error = res2.error
  }

  if (error) {
    throw new Error(error.message || 'Falha ao carregar oportunidades perdidas')
  }

  const raw = (data ?? {}) as any

  const totalToday = Number(raw.total_today ?? 0) || 0
  const visibleToday = Number(raw.visible_today ?? 0) || 0
  const missedToday = Number(raw.missed_today ?? 0) || 0

  return {
    totalToday,
    visibleToday,
    missedToday,
    hasAssetLimit,
  }
}

